// The shared rows of the ☸ OPTIONS menu (ui-map-interface.md §13, R1731).
//
// The sections whose rows are keys of the settings blob — MAP, AUDIO,
// INTERFACE and the two switches of SOCIAL — draw from here in both hosts,
// so the game menu and the hub SETTINGS panel show one row with one name,
// one sentence and one control. `applyPress` is the one rule that turns a press on one of these
// rows into the next settings; a host stores what it answers and applies
// it the way its page does (the game hands the sound to its director, the
// hub to the sink of `ui/audiochoices.ts`).
//
// The language is the one row that does not write at the press: a pick
// opens the confirm strip, because a change reloads the page (R855). The
// host keeps the pending pick and passes it back in; RELOAD NOW is the
// press that writes.
//
// Every choice comes from the choice kit (ui-map-interface.md §18,
// R2011): the desktop chat, the UI scale and the sheet heights are pill
// groups, the chart reading is preview tiles, the UI theme is the one
// stepper, and the language goes through `valueChoice` with the option
// list as its smallest form, so a seventh language turns it into a
// select box.

import { oneOf } from '../../util/oneof';
import {
  DEFAULT_SETTINGS, CASINO_DESKS, CHART_READINGS, CHAT_DESKS, RENDER_SCALE_STEPS, SHEET_HEIGHTS, TAB_LABELS,
  type CasinoDesk, type ChartSetting,
  type ChatDesk,
  type GameSettings,
  snapMarkPct, snapSeaIdleMin,
} from '../settings';
import { MARK_SCALE, SEAVIEW_IDLE } from '../../gen/presentation';
import { pickDesk } from '../chatdock';
import { LANGUAGES } from '../../gen/balance';
import { options as S } from '../../gen/strings';
import { chatStampZone } from '../chatstamp';
import { cachedEntitlements } from '../lookcache';
import { UI_SCALE_STEPS, snapUiScale } from '../uiscale';
import { chartPick } from './chartpick';
import { identityPick } from './identitypick';
import { type Choice, pillGroup, stepper, valueChoice } from '../choice/choice';
import { identityOf, type Identity } from '../../identity/pick';
import { type RangeKey, type VolumeKey, note, percentOf, rangeCtl, row, switchCtl, volumeCtl } from './kit';
import { MARK_KEYS, type MarkMode } from '../../arcade/markscale';
import { uiThemeLabel, uiThemeSteps } from './themepick';
import { casinoHidden } from '../../platform/platform';
import { chatFilterOn } from '../chatfilter';

/** The act names of the shared rows. Each host routes them to `applyPress`
 *  (or, for the language, to its pending pick). */
export const ACT = {
  /** A switch of the blob: `data-key` names it. */
  sw: 'opt-sw',
  /** The chart reading: `data-id` names it. */
  chart: 'opt-chart',
  /** The UI theme: `data-id` is the step, -1 or 1 (R1776). The host
   *  writes the pick through `setThemePick`, not through `applyPress`. */
  theme: 'opt-theme',
  /** The UI scale: `data-id` is the step in percent. */
  scale: 'opt-scale',
  /** The render scale: `data-id` is the step in percent (R484 item 2). */
  render: 'opt-render',
  /** The sheet heights: `data-id` is `two` or `three` (R1748). */
  sheets: 'opt-sheets',
  /** The tab labels: `data-id` is `glyph` or `word` (R2215). */
  tabLabels: 'opt-tablabels',
  /** The desktop chat: `data-id` is `small`, `large` or `docked` (R1838). */
  chatDesk: 'opt-chatdesk',
  /** The desktop casino: `data-id` is `undocked` or `docked` (R2198). */
  casinoDesk: 'opt-casinodesk',
  /** A language pick: `data-id` is `auto` or a tag. It opens the strip. */
  lang: 'opt-lang',
  /** RELOAD NOW: `data-id` is the picked language. It writes. */
  langGo: 'opt-lang-go',
  /** CANCEL: the strip closes and nothing is written. */
  langNo: 'opt-lang-no',
  /** An identity pick (R3298): `data-id` is `dark` or `light`. It opens
   *  the strip, because a change reloads the page. */
  identity: 'opt-identity',
  /** RELOAD NOW: `data-id` is the picked identity. It writes. */
  identityGo: 'opt-identity-go',
  /** CANCEL: the strip closes and nothing is written. */
  identityNo: 'opt-identity-no',
} as const;

/** The switches of the blob these rows carry. The dev stats switch stands
 *  in HELP, a section of the game menu alone (R1746). */
export const SWITCH_KEYS = [
  'screenshake', 'fluff', 'sonar', 'musicOn', 'sfxOn', 'audioTakeover', 'devStats', 'chatCountOn', 'inboxCountOn',
  'chatTimeOn', 'seaIdle', 'seaVeil', 'seaLife', 'seaPower', 'mirrorDesk', 'tabDots',
] as const;
export type SwitchKey = (typeof SWITCH_KEYS)[number];

/** The word of each chart reading under its picture. */
const CHART_WORDS: Record<ChartSetting, string> = {
  off: S.chart_off,
  historical: S.chart_historical,
  modern: S.chart_modern,
};

/** True when a key of the blob holds another value than the default. */
export function changed<K extends keyof GameSettings>(s: GameSettings, key: K): boolean {
  return s[key] !== DEFAULT_SETTINGS[key];
}

/** One switch row of the blob. */
export function switchRow(s: GameSettings, key: SwitchKey, name: string, what: string): string {
  return row({
    name, what, changed: changed(s, key),
    control: switchCtl(s[key], { act: ACT.sw, key }, { label: name }),
  });
}

/** The words of the two identities (R3298). */
const IDENTITY_WORDS: Record<Identity, string> = {
  dark: S.identity_dark,
  light: S.identity_light,
};

/**
 * The IDENTITY row (ui-map-interface.md §2.5, R3298, R3316): DARK or LIGHT
 * as two preview tiles (`identitypick.ts`). A pick opens the confirm
 * strip, because the identity is picked once at page load and a change
 * reloads the page; RELOAD NOW is the press that writes. `pending` is the pick that waits, or null.
 */
function identityRow(s: GameSettings, pending: Identity | null): string {
  return row({
    name: S.row_identity, what: S.row_identity_what, wrap: true, changed: changed(s, 'identity'),
    control: identityPick(pending ?? s.identity, s.identity, ACT.identity, IDENTITY_WORDS, {
      label: S.row_identity,
      confirm: pending === null ? undefined : {
        text: S.identity_confirm,
        go: { act: ACT.identityGo, id: pending, label: S.lang_go },
        no: { act: ACT.identityNo, label: S.lang_no },
      },
    }),
  });
}

/** The MAP rows: the identity (R3298), the chart reading as three
 *  pictures, screen shake, sonar visuals, ambient fluff and the render
 *  scale. No live preview of the map (R1731, card 13 is not built).
 *  `pendingIdentity` is the identity pick that waits for RELOAD NOW. */
export function mapRows(s: GameSettings, o: { pendingIdentity?: Identity | null } = {}): string {
  return identityRow(s, o.pendingIdentity ?? null) + row({
    name: S.row_chart, what: S.row_chart_what, wrap: true, changed: changed(s, 'chart'),
    control: chartPick(s.chart, ACT.chart, CHART_WORDS, S.row_chart),
  })
    + switchRow(s, 'screenshake', S.row_shake, S.row_shake_what)
    + switchRow(s, 'sonar', S.row_sonar, S.row_sonar_what)
    + switchRow(s, 'fluff', S.row_fluff, S.row_fluff_what)
    + renderScaleRow(s);
}

/** The render scale row (R484 item 2): a pill group of the steps, the map
 *  as it is drawn first. */
function renderScaleRow(s: GameSettings): string {
  const steps: Choice[] = RENDER_SCALE_STEPS.map((p) => ({ id: String(p), label: S.scale_step({ percent: p }) }));
  return row({
    name: S.row_render, what: S.row_render_what, changed: changed(s, 'renderScale'),
    control: pillGroup(steps, String(s.renderScale), ACT.render, { label: S.row_render }),
  });
}

/** One volume row: the name and the sentence, then the mute glyph, the
 *  range and the figure on a line of their own. The changed dot reads the
 *  switch alone: a dot that read the level would change the markup when a
 *  drag crosses the default, and the host would rebuild the range under
 *  the finger. */
function volumeRow(s: GameSettings, on: 'musicOn' | 'sfxOn', vol: VolumeKey, name: string, what: string): string {
  return row({
    name, what, wrap: true, changed: changed(s, on),
    control: volumeCtl({ key: vol, on: s[on], name, mute: { act: ACT.sw, key: on } }),
  });
}

/**
 * The AUDIO rows: music, effects and the sound takeover. `player` is the
 * host's own music player row, which stands after the effects; a host
 * with no music passes none. A host that publishes no sound draws one
 * line in place of the rows. `sfxWhat` is the line of the effects row of
 * a host whose effects are not the season's: the arcade match (R2060).
 */
export function audioRows(s: GameSettings, o: { sound: boolean; player?: string; sfxWhat?: string }): string {
  if (!o.sound) return note(S.no_audio);
  return volumeRow(s, 'musicOn', 'musicVol', S.row_music, S.row_music_what)
    + volumeRow(s, 'sfxOn', 'sfxVol', S.row_sfx, o.sfxWhat ?? S.row_sfx_what)
    + (o.player ?? '')
    + switchRow(s, 'audioTakeover', S.row_takeover, S.row_takeover_what);
}

/**
 * The SEA VIEW rows (R2865, ui-map-interface.md §13): the switch IDLE SEA
 * VIEW, the bar AFTER of the idle time only while the switch is ON, and
 * the look switches of the sea view, SURVEY VEIL and SEA LIFE (R2881) and
 * POWER SAVER (R2922). A
 * press of the switch rebuilds the menu, so the bar comes and goes with
 * it. The bar carries no changed dot: a dot that read the minutes would
 * change the markup when a drag crosses the default, and the host would
 * rebuild the range under the finger.
 */
export function seaviewRows(s: GameSettings): string {
  const bar = s.seaIdle
    ? row({
      name: S.row_seaidle_min, what: S.row_seaidle_min_what, wrap: true,
      control: `<span class="o-vol">${rangeCtl({
        key: 'seaIdleMin', min: SEAVIEW_IDLE.minMin, max: SEAVIEW_IDLE.maxMin, step: SEAVIEW_IDLE.stepMin,
        label: S.seaidle_min_label, wide: true,
      })}</span>`,
    })
    : '';
  return switchRow(s, 'seaIdle', S.row_seaidle, S.row_seaidle_what) + bar
    + switchRow(s, 'seaVeil', S.row_seaveil, S.row_seaveil_what)
    + switchRow(s, 'seaLife', S.row_sealife, S.row_sealife_what)
    + switchRow(s, 'seaPower', S.row_seapower, S.row_seapower_what);
}

/** The settings a pill group row picks, one word a value. */
type PillKey = 'chatDesk' | 'casinoDesk' | 'tabLabels' | 'sheetHeights';

/** One pill group row of the blob: one pill a value, in the order of
 *  `values`, each named by its word. */
function pillRow<K extends PillKey>(s: GameSettings, key: K, values: readonly GameSettings[K][],
  words: Readonly<Record<GameSettings[K], string>>, act: string, name: string, what: string): string {
  const list: Choice[] = values.map((v) => ({ id: v, label: words[v] }));
  return row({ name, what, changed: changed(s, key), control: pillGroup(list, s[key], act, { label: name }) });
}

/** The words of the three values of the desktop chat (R1838). */
const CHAT_DESK_WORDS: Record<ChatDesk, string> = {
  small: S.chat_desk_small,
  large: S.chat_desk_large,
  docked: S.chat_desk_docked,
};

/** The row of the desktop chat setting (R1838): SMALL, LARGE or DOCKED. */
function chatDeskRow(s: GameSettings): string {
  return pillRow(s, 'chatDesk', CHAT_DESKS, CHAT_DESK_WORDS, ACT.chatDesk, S.row_chat_desk, S.row_chat_desk_what);
}

/** The words of the two values of the desktop casino (R2198). */
const CASINO_DESK_WORDS: Record<CasinoDesk, string> = {
  undocked: S.casino_desk_undocked,
  docked: S.casino_desk_docked,
};

/** The row of the desktop casino setting (R2198): UNDOCKED or DOCKED. */
function casinoDeskRow(s: GameSettings): string {
  return pillRow(s, 'casinoDesk', CASINO_DESKS, CASINO_DESK_WORDS, ACT.casinoDesk,
    S.row_casino_desk, S.row_casino_desk_what);
}

/** The SOCIAL rows of the blob (R1759): the desktop chat setting (R1838),
 *  the switch of the unread chat count, the switch of the unread INBOX
 *  count, the switch of the chat time stamp, whose line names the zone
 *  of the device (R1791), and the switch of the chat filter (R2937).
 *  Each host puts its block list under them. The game menu of a
 *  telephone leaves the desktop chat out (`desk` false): its chat is the
 *  band. The hub keeps it on every screen, as it keeps the
 *  sheet heights of a telephone. */
export function socialRows(s: GameSettings, o: { desk?: boolean } = {}): string {
  const zone = chatStampZone(Math.floor(Date.now() / 1000));
  return (o.desk === false ? '' : chatDeskRow(s))
    + switchRow(s, 'chatCountOn', S.row_chat_count, S.row_chat_count_what)
    + switchRow(s, 'inboxCountOn', S.row_inbox_count, S.row_inbox_count_what)
    + switchRow(s, 'chatTimeOn', S.row_chat_time, S.row_chat_time_what({ zone }))
    + chatFilterRow(s);
}

/** The key of the chat filter switch: `ACT.sw` with this key flips it. */
export const CHAT_FILTER_KEY = 'chatFilter';

/** The row of the chat filter (R2937): a switch that shows the filter in
 *  force, the platform's default where the player made no choice. The
 *  changed dot marks a choice of the player. */
function chatFilterRow(s: GameSettings): string {
  return row({
    name: S.row_chat_filter, what: S.row_chat_filter_what, changed: changed(s, 'chatFilter'),
    control: switchCtl(chatFilterOn(s), { act: ACT.sw, key: CHAT_FILTER_KEY }, { label: S.row_chat_filter }),
  });
}

/** The language choices: each language by its own name, with no AUTO
 *  line (i18n.md §2, R2207). */
export function languageChoices(): Choice[] {
  return LANGUAGES.map((l) => ({ id: l.tag as string, label: l.endonym as string }));
}

/** The mark scale rows in their fixed order (R1992): the arcade, then
 *  the core game. */
export const MARK_MODES: readonly MarkMode[] = ['arcade', 'core'];

/** One mark scale row (R1981, R1992, R3246): the slider of one mode, on
 *  the steps of `[presentation.mark_scale]`, then its percent. The row
 *  carries no changed dot, as the idle time bar does not: a dot that read
 *  the percent would change the markup when a drag crosses 100, and the
 *  host would rebuild the range under the finger. */
function markRow(mode: MarkMode): string {
  const name = mode === 'arcade' ? S.row_mark_arcade : S.row_mark_core;
  const what = mode === 'arcade' ? S.row_mark_arcade_what : S.row_mark_core_what;
  return row({
    name, what, wrap: true,
    control: `<span class="o-vol">${rangeCtl({
      key: MARK_KEYS[mode], min: MARK_SCALE.minPct, max: MARK_SCALE.maxPct, step: MARK_SCALE.stepPct,
      label: name, wide: true,
    })}</span>`,
  });
}

/** The words of the two sheet heights (R1748). */
const SHEET_WORDS: Record<GameSettings['sheetHeights'], string> = {
  two: S.sheets_two,
  three: S.sheets_three,
};

/** The words of the two tab labels (R2215). */
const TAB_LABEL_WORDS: Record<GameSettings['tabLabels'], string> = {
  glyph: S.tabs_glyph,
  word: S.tabs_word,
};

/** The row of TAB LABELS (R2215, round 102 card 17): GLYPH or
 *  GLYPH + WORD, for the telephone tab bar and the rail. */
function tabLabelsRow(s: GameSettings): string {
  return pillRow(s, 'tabLabels', TAB_LABELS, TAB_LABEL_WORDS, ACT.tabLabels, S.row_tabs, S.row_tabs_what);
}

/**
 * The INTERFACE rows: the UI theme as a stepper (R1776), the UI scale as a
 * pill group, the mark scale sliders the host names (R1992, R3246), the sheet
 * heights of a telephone as a pill group (R1748), the tab labels of a
 * telephone as a pill group (R2215), the desktop casino (R2198), the
 * MIRRORED LAYOUT switch of a desktop (R2883), and the language as an
 * option list, or a select box past `choice_list_max` languages (R2011).
 * `pendingLang` is the pick that waits for RELOAD NOW, or null; while it
 * stands, the strip opens under it. `held` is the
 * entitlement set the theme values read, by default the cached one.
 */
export function ifaceRows(s: GameSettings, o: {
  pendingLang?: string | null; held?: ReadonlySet<string>;
  /** The rows to draw, in their fixed order; absent is all eight. The
   *  arcade match menu draws the scale and the language (R1936). */
  rows?: readonly IfaceRowId[];
  /** False leaves the desktop casino row out (R2198): the game menu of a
   *  telephone, whose casino is the sheet. The hub keeps it on every
   *  screen, as it keeps the desktop chat row. */
  desk?: boolean;
  /** The mark scale rows after the UI scale row, in `MARK_MODES` order
   *  (R1992): the season menu draws the core row, the arcade match menu
   *  the arcade row, the hub both. Absent is none. */
  marks?: readonly MarkMode[];
} = {}): string {
  const steps: Choice[] = UI_SCALE_STEPS.map((p) => ({ id: String(p), label: S.scale_step({ percent: p }) }));
  const pending = o.pendingLang ?? null;
  const held = o.held ?? cachedEntitlements();
  const want = (id: IfaceRowId) => o.rows === undefined || o.rows.includes(id);
  return (want('theme') ? row({
    name: S.row_theme, what: S.row_theme_what, changed: changed(s, 'theme'),
    control: stepper(uiThemeLabel(s, held), ACT.theme, {
      label: S.row_theme, still: uiThemeSteps(s, held).length < 2,
      prev: S.theme_prev, next: S.theme_next,
    }),
  }) : '')
    + (want('scale') ? row({
      name: S.row_scale, what: S.row_scale_what, changed: changed(s, 'uiScale'),
      control: pillGroup(steps, String(s.uiScale), ACT.scale, { label: S.row_scale }),
    }) : '')
    + MARK_MODES.filter((m) => o.marks?.includes(m)).map(markRow).join('')
    + (want('sheets')
      ? pillRow(s, 'sheetHeights', SHEET_HEIGHTS, SHEET_WORDS, ACT.sheets, S.row_sheets, S.row_sheets_what) : '')
    + (want('tabs') ? tabLabelsRow(s) : '')
    + (want('dots') ? switchRow(s, 'tabDots', S.row_tab_dots, S.row_tab_dots_what) : '')
    // A store app hides the casino, and its row with it (R2927).
    + (want('casino') && o.desk !== false && !casinoHidden() ? casinoDeskRow(s) : '')
    // The mirrored desktop (R2883) acts on a desktop alone, so a telephone
    // menu leaves it out, as it leaves out the desktop casino.
    + (want('mirror') && o.desk !== false ? switchRow(s, 'mirrorDesk', S.row_mirror, S.row_mirror_what) : '')
    // The language row holds no control of its own: the option list under
    // it is the control, one line a language.
    + (!want('lang') ? '' : row({ name: S.row_lang, what: S.row_lang_what, control: '', changed: changed(s, 'lang') })
    + valueChoice({
      list: languageChoices(), chosen: pending ?? s.lang, act: ACT.lang, floor: 'list',
      now: s.lang,
      label: S.row_lang,
      confirm: pending === null ? undefined : {
        text: S.lang_confirm,
        go: { act: ACT.langGo, id: pending, label: S.lang_go },
        no: { act: ACT.langNo, label: S.lang_no },
      },
    }));
}

/** The eight rows of INTERFACE, in their fixed order. */
export type IfaceRowId = 'theme' | 'scale' | 'sheets' | 'tabs' | 'dots' | 'casino' | 'mirror' | 'lang';

/** The settings with the value of `id` when it is one of `values` and
 *  not the value in force; else null. */
function pickOne<K extends 'chart' | 'sheetHeights' | 'tabLabels' | 'casinoDesk'>(
  s: GameSettings, key: K, values: readonly GameSettings[K][], id: string | undefined,
): GameSettings | null {
  const v = oneOf(values, id);
  return v !== undefined && v !== s[key] ? { ...s, [key]: v } : null;
}

/** The tags the language rows may write. */
function langOk(id: string): id is GameSettings['lang'] {
  return LANGUAGES.some((l) => l.tag === id);
}

/**
 * The next settings after one press on a shared row, or null when the
 * press writes nothing: an act that is not a shared write, a key it does
 * not carry, or a language pick that waits for its confirm. `data` is the
 * pressed element's `dataset`.
 */
export function applyPress(s: GameSettings, act: string,
  data: { key?: string; id?: string }): GameSettings | null {
  switch (act) {
    case ACT.sw: {
      // The chat filter writes the player's choice, the opposite of the
      // filter in force (R2937).
      if (data.key === CHAT_FILTER_KEY) return { ...s, chatFilter: chatFilterOn(s) ? 'off' : 'on' };
      const key = oneOf(SWITCH_KEYS, data.key);
      return key ? { ...s, [key]: !s[key] } : null;
    }
    case ACT.chart: return pickOne(s, 'chart', CHART_READINGS, data.id);
    case ACT.scale: {
      const p = Number(data.id);
      if (!UI_SCALE_STEPS.includes(p)) return null;
      const next = snapUiScale(p);
      return next === s.uiScale ? null : { ...s, uiScale: next };
    }
    case ACT.render: {
      const p = Number(data.id);
      if (!RENDER_SCALE_STEPS.includes(p) || p === s.renderScale) return null;
      return { ...s, renderScale: p };
    }
    case ACT.sheets: return pickOne(s, 'sheetHeights', SHEET_HEIGHTS, data.id);
    case ACT.tabLabels: return pickOne(s, 'tabLabels', TAB_LABELS, data.id);
    case ACT.chatDesk: {
      const d = oneOf(CHAT_DESKS, data.id);
      return d && d !== s.chatDesk ? { ...s, ...pickDesk(s, d) } : null;
    }
    case ACT.casinoDesk: return pickOne(s, 'casinoDesk', CASINO_DESKS, data.id);
    case ACT.langGo: {
      const id = data.id ?? '';
      return langOk(id) && id !== s.lang ? { ...s, lang: id } : null;
    }
    case ACT.identityGo: {
      const id = identityOf(data.id);
      return id !== null && id !== s.identity ? { ...s, identity: id } : null;
    }
    default:
      return null;
  }
}

/** A range's value, in percent, as the gain the blob holds. */
export function gainOf(raw: string): number {
  const pct = Number(raw);
  return Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) / 100 : 0;
}

/** The keys a range writes, in the one order the hosts wire them. */
export const RANGE_KEYS: readonly RangeKey[] = ['musicVol', 'sfxVol', 'seaIdleMin', 'markScaleArcade', 'markScaleCore'];

/** True when a range key holds a mark scale (R3246). */
function isMarkKey(key: RangeKey): key is 'markScaleArcade' | 'markScaleCore' {
  return key === 'markScaleArcade' || key === 'markScaleCore';
}

/** True when `raw` names a key a range writes. */
export function isRangeKey(raw: unknown): raw is RangeKey {
  return typeof raw === 'string' && (RANGE_KEYS as readonly string[]).includes(raw);
}

/** A range's raw value as the blob holds it: a gain for a volume, whole
 *  minutes on the bar for the idle time (R2865), a step of the slider for
 *  a mark scale (R3246). */
export function rangeValue(key: RangeKey, raw: string): number {
  if (key === 'seaIdleMin') return snapSeaIdleMin(Number(raw));
  if (isMarkKey(key)) return snapMarkPct(Number(raw));
  return gainOf(raw);
}

/** The step of a range a value stands on: the hub sounds one cue for each
 *  step a drag crosses, and not one for each event. A volume has twenty
 *  steps; the idle time steps by the minute and a mark scale by its
 *  percent step. */
export function rangeStep(key: RangeKey, v: number): number {
  return key === 'seaIdleMin' || isMarkKey(key) ? v : Math.round(v * 20);
}

/**
 * Writes the ranges onto the marks under `root`: each range's value, each
 * figure and each level bar of a quick tile. A host calls it after every
 * rebuild of its markup and on every `input` of a range, because the
 * markup carries no value (see `kit.ts`).
 */
export function syncRangeMarks(root: ParentNode, s: GameSettings): void {
  for (const key of RANGE_KEYS) {
    const pos = key === 'seaIdleMin' || isMarkKey(key) ? s[key] : percentOf(s[key]);
    const fig = key === 'seaIdleMin' ? S.seaidle_min_fig({ min: pos })
      : isMarkKey(key) ? S.scale_step({ percent: pos }) : String(pos);
    for (const r of root.querySelectorAll<HTMLInputElement>(`input[data-key="${key}"]`)) r.value = String(pos);
    for (const f of root.querySelectorAll<HTMLElement>(`[data-vol="${key}"]`)) f.textContent = fig;
    for (const b of root.querySelectorAll<HTMLElement>(`[data-volbar="${key}"]`)) b.style.width = `${pos}%`;
  }
}
